// apps/web/src/layouts/components/NotificationBellDropdown.tsx
//
// Sininho de notificações do usuário autenticado.
// Contrato: docs/contracts/ESCR-public-quote-consultor-notify.md.
//
// Consome a inbox completa (GET /notifications) — histórico persistido + estado
// de não lidas + deep-link armazenado (item.link). Realtime via Socket.IO
// `notifications.created` (room user:${userId}): invalida a query + mostra toast.

import { useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Bell, Car, CheckCheck, LifeBuoy, Megaphone, UserPlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  announcementsKeys,
  notificationsInboxKeys,
  queryKeys,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useSocketEvent,
  useSocketRoom,
} from '@alamo/api';
import { useAuth } from '@alamo/auth';
import type { AlamoNotification, NotificationCreatedEvent } from '@alamo/types';
import { Popover, PopoverContent, PopoverTrigger } from '@alamo/ui';

import {
  AdminDailyDigestBody,
  isAdminDailyDigestType,
  readAdminDailyDigestMetrics,
} from './AdminDailyDigestBody';
import { WebPushToggle } from './WebPushToggle';

/**
 * Format a timestamp as a short relative-time string (pt-BR friendly).
 */
function formatRelative(at: string, locale: string): string {
  const ms = Date.now() - new Date(at).getTime();
  const minutes = Math.round(ms / 60_000);
  const isPt = locale.startsWith('pt');
  if (minutes < 1) return isPt ? 'agora' : 'just now';
  if (minutes < 60) return isPt ? `${minutes} min atras` : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return isPt ? `${hours}h atras` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  return isPt ? `${days}d atras` : `${days}d ago`;
}

/** Shape mínimo compartilhado entre a linha da inbox e o payload realtime. */
type NotificationLike = Pick<AlamoNotification, 'type' | 'link' | 'metadata'>;

/**
 * Resolve o destino de navegação de uma notificação.
 * Honra o `link` armazenado pelo backend. Fallbacks por tipo quando `link` ausente:
 * - `announcement_published` → /novidades/:announcementId (deep-link do comunicado)
 * - `change_set.*` (metadata.changeSetId) → fila de aprovações de precificação.
 * - `support_ticket_created` → /admin/support/:ticketId (atendente vê a fila)
 * - `support_ticket_status_changed` → /support/tickets/:ticketId (dono acompanha o status)
 * - `fipe_queue_items_pending` → /admin/vehicle-catalog?tab=queue (fila de classificação FIPE)
 * - `consultant.stalled_digest` → propostas paradas, já filtradas.
 */
function resolveLink(n: NotificationLike): string | null {
  if (n.link) return n.link;
  if (n.type === 'consultant.stalled_digest') {
    return '/proposals?quickFilter=stalled&lifecycle=active';
  }
  if (n.type === 'fipe_queue_items_pending') return '/admin/vehicle-catalog?tab=queue';
  const announcementId =
    typeof n.metadata?.announcementId === 'string' ? n.metadata.announcementId : null;
  if (n.type === 'announcement_published' && announcementId) {
    return `/novidades/${announcementId}`;
  }
  const ticketId = typeof n.metadata?.ticketId === 'string' ? n.metadata.ticketId : null;
  if (n.type === 'support_ticket_created' && ticketId) return `/admin/support/${ticketId}`;
  if (n.type === 'support_ticket_status_changed' && ticketId) return `/support/tickets/${ticketId}`;
  const changeSetId = typeof n.metadata?.changeSetId === 'string' ? n.metadata.changeSetId : null;
  if (n.type.startsWith('change_set.') || changeSetId) {
    return changeSetId
      ? `/admin/pricing/approvals?csId=${changeSetId}`
      : '/admin/pricing/approvals';
  }
  return null;
}

/**
 * Corpo de notificação pronto para uma superfície de texto puro.
 *
 * O corpo de `announcement_published` é uma cópia do resumo do comunicado, que é
 * rich-text — o sininho e o toast não interpretam HTML, então a marcação sai em
 * vez de vazar como texto. O achatamento é restrito a esse tipo: os demais já
 * nascem em texto puro e um strip cego comeria trechos legítimos (`5 < 10`).
 */
const RICH_TEXT_BODY_TYPES = new Set(['announcement_published']);
const HTML_TAG_RE = /<\/?[a-z][^>]*>/gi;
const BASIC_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&nbsp;': ' ',
  '&amp;': '&',
};

function notificationBodyText(type: string, body: string | null | undefined): string {
  if (!body) return '';
  if (!RICH_TEXT_BODY_TYPES.has(type)) return body;
  return body
    .replace(HTML_TAG_RE, ' ')
    .replace(/&(?:lt|gt|quot|apos|nbsp|amp|#39);/g, (entity) => BASIC_ENTITIES[entity] ?? entity)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Ícone do toast realtime por tipo de notificação (default: sino). */
function toastIconFor(type: string): LucideIcon {
  if (type === 'announcement_published') return Megaphone;
  if (type === 'support_ticket_created' || type === 'support_ticket_status_changed') {
    return LifeBuoy;
  }
  if (type === 'fipe_queue_items_pending') return Car;
  if (type === 'referral_signup' || type === 'referral_approved') return UserPlus;
  return Bell;
}

export function NotificationBellDropdown() {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data } = useNotifications({ limit: 30 });
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const unreadCount = data?.unreadCount ?? 0;

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // ─── Realtime (Socket.IO `notifications.created`, room user:${userId}) ────────
  useSocketRoom(user?.id ? `user:${user.id}` : null);

  // Throttle de toasts: máx 3 simultâneos; toast só no evento realtime (nunca no
  // poll/refetch) — evita loop de toast.
  const activeToastIds = useRef<string[]>([]);

  useSocketEvent<NotificationCreatedEvent>('notifications.created', (payload) => {
    if (!payload?.id) return;

    // Re-fetch da inbox + badge (best-effort — query também tem polling).
    queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    if (payload.type === 'registration_correction_requested') {
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    }

    // Comunicado publicado → refresca feed/badge/surfaces de Novidades.
    if (payload.type === 'announcement_published') {
      void queryClient.invalidateQueries({ queryKey: announcementsKeys.all });
    }

    if (activeToastIds.current.length >= 3) {
      const oldest = activeToastIds.current.shift();
      if (oldest) toast.dismiss(oldest);
    }
    const toastId = payload.id;
    activeToastIds.current.push(toastId);

    const target = resolveLink(payload);
    const ToastIcon = toastIconFor(payload.type);
    const bodyText = notificationBodyText(payload.type, payload.body);
    const digestMetrics = isAdminDailyDigestType(payload.type)
      ? readAdminDailyDigestMetrics(payload.metadata)
      : null;
    const toastBody = digestMetrics
      ? t('common:notifications.daily_digest.toast_summary', {
          newQuotations: digestMetrics.newQuotationsCount,
          acceptedQuotations: digestMetrics.acceptedQuotationsCount,
          paidActivationFees: digestMetrics.paidActivationFeesCount,
        })
      : bodyText;
    toast(
      (toastInstance) => (
        <button
          type="button"
          className="flex items-start gap-2 text-left"
          onClick={() => {
            toast.dismiss(toastInstance.id);
            if (target) navigate(target);
          }}
        >
          <ToastIcon className="mt-0.5 size-4 shrink-0 text-laranja-encanto" aria-hidden />
          <div>
            <p className="text-sm font-medium text-ink">{payload.title}</p>
            {toastBody && <p className="text-xs text-slate-600">{toastBody}</p>}
            {target && (
              <span className="mt-0.5 inline-block text-xs font-semibold text-laranja-encanto">
                {t('common:notifications.view')}
              </span>
            )}
          </div>
        </button>
      ),
      { id: toastId, duration: 5000 },
    );
  });

  // ─── Click numa notificação: marca lida + navega pro deep-link ───────────────
  function handleOpenNotification(n: AlamoNotification): void {
    if (!n.isRead) {
      markRead.mutate(n.id);
    }
    const target = resolveLink(n);
    if (target) navigate(target);
  }

  function handleMarkAllRead(): void {
    if (unreadCount === 0) return;
    markAllRead.mutate();
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Popover>
      <PopoverTrigger>
        <button
          type="button"
          className="relative size-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
          aria-label={t('common:notifications.aria_label', { count: unreadCount })}
          aria-haspopup="dialog"
          data-testid="notification-bell"
        >
          <Bell className="size-5 text-slate-600" />
          {unreadCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-4 h-4 bg-erro-apoio text-white text-[10px] rounded-full px-1 flex items-center justify-center"
              aria-hidden="true"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[calc(100vw-2rem)] max-w-sm p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-secondary-950">
            {t('common:notifications.heading')}
          </h3>
          {unreadCount > 0 && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-laranja-encanto hover:underline disabled:opacity-50"
              onClick={handleMarkAllRead}
              disabled={markAllRead.isPending}
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="size-3.5" aria-hidden />
              {t('common:notifications.mark_all_read')}
            </button>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="p-8 flex flex-col items-center text-center">
            <Bell className="size-12 text-slate-300" aria-hidden />
            <h4 className="text-sm font-bold text-secondary-950 mt-3">
              {t('common:notifications.empty.heading')}
            </h4>
            <p className="text-xs text-slate-600 mt-1">{t('common:notifications.empty.body')}</p>
          </div>
        ) : (
          <ul className="max-h-96 overflow-auto divide-y divide-slate-200">
            {rows.map((n) => {
              const bodyText = notificationBodyText(n.type, n.body);
              const hasStructuredDigest =
                isAdminDailyDigestType(n.type) && readAdminDailyDigestMetrics(n.metadata) !== null;
              const content = (
                <div className="flex items-start gap-2">
                  {!n.isRead && (
                    <span
                      className="mt-1.5 size-2 shrink-0 rounded-full bg-laranja-encanto"
                      aria-hidden
                    />
                  )}
                  <div className={`min-w-0 flex-1 ${n.isRead ? 'pl-4' : ''}`}>
                    <div
                      className={`text-sm ${n.isRead ? 'font-medium text-slate-600' : 'font-bold text-ink'}`}
                    >
                      {n.title}
                    </div>
                    {hasStructuredDigest ? (
                      <AdminDailyDigestBody metadata={n.metadata} />
                    ) : bodyText ? (
                      <div className="whitespace-pre-wrap break-words text-xs text-slate-500">
                        {bodyText}
                      </div>
                    ) : null}
                    <div className="text-xs text-slate-400 mt-0.5">
                      {formatRelative(n.createdAt, i18n.language)}
                    </div>
                  </div>
                </div>
              );

              return (
                <li
                  key={n.id}
                  className="p-3 hover:bg-slate-50"
                  data-testid={`notification-entry-${n.id}`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => handleOpenNotification(n)}
                  >
                    {content}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <WebPushToggle />
      </PopoverContent>
    </Popover>
  );
}
